import type { BookingPreferences, SelectedSlot } from "../bookingFlowMachine"
import {
  BookedAppointment,
  bookingIneligibilityReason,
  ScreeningProgram,
  useBookAppointment,
  useBookingOffering
} from "../hooks"
import { appointmentTypeCopy, FOLLOW_UP_HELPER_TEXT, offeringSummaryLine } from "../preferencesCopy"
import { AppointmentAvailability } from "./AppointmentAvailability"
import { VisitTypeCard } from "./VisitTypeCard"
import { UsaStatesAndDc } from "@internal/constants"
import { Alert, Box, Breadcrumb, Breadcrumbs, Divider, Progress, Typography } from "@carrotfertility/carotene-core"
import {
  Form,
  FormAutocomplete,
  FormButton,
  FormRadioGroup,
  type FormSelectOption,
  useFormContext,
  useWatch
} from "@carrotfertility/carotene-core-x"
import React from "react"
import { useIntl } from "react-intl"

type SelectPreferencesPageProps = {
  program: ScreeningProgram
  onConfirmBooking: (preferences: BookingPreferences, slot: SelectedSlot, appointment: BookedAppointment) => void
  onDisqualified: (exclusionMessageId: string) => void
  onReturnToProviderResults?: () => void
}

type PreferencesFormValues = {
  offeringId: string
  // FormAutocomplete stores the selected option object (or null), not a bare value.
  region: FormSelectOption | null
  // Set by AppointmentAvailability once the member picks a date + time.
  selectedDate: string | null
  selectedSlot: SelectedSlot | null
}

// The screen's own copy is English literal (see preferencesCopy.ts); the location-of-care
// options are localized here from the shared @internal/constants state list.
export function SelectPreferencesPage({
  program,
  onConfirmBooking,
  onDisqualified,
  onReturnToProviderResults
}: SelectPreferencesPageProps): JSX.Element {
  const intl = useIntl()
  const offeringQuery = useBookingOffering(program)
  const bookAppointment = useBookAppointment()

  if (offeringQuery.isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center" }}>
        <Progress />
      </Box>
    )
  }

  if (offeringQuery.isError || !offeringQuery.data) {
    return <Alert severity="error">Something went wrong loading your options. Please try again.</Alert>
  }

  const bookingOffering = offeringQuery.data
  const offerings = bookingOffering.offerings
  // The summary line and default selection follow the primary bookable offering (the initial
  // consult on a first visit).
  const primaryOffering = offerings.find((offering) => offering.bookable) ?? offerings[0]

  // 50 states + DC only: a clinician cannot hold a state medical license in a territory, so
  // offering one would be a dead end.
  const regionOptions = UsaStatesAndDc.map((state) => ({ value: state.id, label: intl.formatMessage(state.label) }))

  const handleSubmit = async (values: PreferencesFormValues) => {
    // The Confirm button is disabled until a slot is chosen; guard anyway.
    if (!values.selectedSlot) return
    const slot = values.selectedSlot
    try {
      // Place the booking, then advance — the confirmation screen needs the returned
      // appointment (assigned provider + visit window). A failure surfaces inline (see
      // AppointmentAvailability) and leaves the member here to retry.
      const appointment = await bookAppointment.mutateAsync({
        program,
        slotToken: slot.slotToken,
        // The member doesn't pick a provider; take the first eligible host for the slot.
        hostId: slot.availableHostIds[0]
      })
      onConfirmBooking(
        {
          offeringId: values.offeringId,
          region: values.region ? String(values.region.value) : ""
        },
        slot,
        appointment
      )
    } catch (error) {
      // A terminal eligibility rejection routes out; any other failure leaves the member here with
      // bookAppointment.isError driving the inline retry (MEM-771).
      const ineligibleReason = bookingIneligibilityReason(error)
      if (ineligibleReason !== null) {
        onDisqualified(ineligibleReason)
      }
    }
  }

  return (
    <Form<PreferencesFormValues>
      onSubmit={handleSubmit}
      defaultValues={{ offeringId: primaryOffering?.id ?? "", region: null, selectedDate: null, selectedSlot: null }}
    >
      <Box
        sx={(theme) => ({
          display: "flex",
          flexDirection: "column",
          gap: theme.spacing(theme.tokens.spacing.lg),
          maxInlineSize: 640
        })}
      >
        <Box
          sx={(theme) => ({ display: "flex", flexDirection: "column", gap: theme.spacing(theme.tokens.spacing.sm) })}
        >
          <Breadcrumbs>
            {onReturnToProviderResults ? (
              <Breadcrumb component="button" type="button" onClick={onReturnToProviderResults}>
                Provider results
              </Breadcrumb>
            ) : (
              <Breadcrumb>Provider results</Breadcrumb>
            )}
            <Breadcrumb currentPage>Book now</Breadcrumb>
          </Breadcrumbs>
          <Divider />
          <Alert severity="error" sx={(theme) => ({ marginBlockStart: theme.spacing(theme.tokens.spacing.lg) })}>
            For emergencies, please call 911 or seek immediate medical attention.
          </Alert>
        </Box>

        <Box
          sx={(theme) => ({ display: "flex", flexDirection: "column", gap: theme.spacing(theme.tokens.spacing.xxs) })}
        >
          <Typography variant="h1">Select your preferences to see available appointment times.</Typography>
          {primaryOffering && (
            <Typography color="text.secondary">
              {offeringSummaryLine(bookingOffering, primaryOffering.durationMinutes)}
            </Typography>
          )}
        </Box>

        <FormRadioGroup
          name="offeringId"
          label="What kind of help do you need?"
          controllerProps={{ rules: { required: "Please select a visit type." } }}
          helperText={FOLLOW_UP_HELPER_TEXT}
          radioGroupProps={{
            row: true,
            sx: (theme) => ({
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
              gap: theme.spacing(theme.tokens.spacing.sm)
            })
          }}
          options={offerings.map((offering) => ({
            value: offering.id,
            disabled: !offering.bookable,
            label: appointmentTypeCopy(offering.appointmentType).title
          }))}
          renderItem={({ radioProps }) => {
            const offering = offerings.find((candidate) => candidate.id === radioProps.value)
            return offering ? <VisitTypeCard key={offering.id} offering={offering} radioProps={radioProps} /> : null
          }}
        />

        <Box sx={{ maxInlineSize: 440 }}>
          <FormAutocomplete
            name="region"
            label="Location of care"
            controllerProps={{ rules: { required: "Please select your location of care." } }}
            fullWidth
            options={regionOptions}
            helperText="Select the state where you'll be during your visit"
          />
        </Box>

        <AvailabilityStep
          program={program}
          booking={{ isPending: bookAppointment.isLoading, isError: bookAppointment.isError }}
        />
      </Box>
    </Form>
  )
}

// Two-phase, on one screen: the member first submits their visit type + location ("See
// availability"), which reveals the date + time picker (AppointmentAvailability owns that and
// the "Confirm booking"/"Continue" CTA). Lives inside <Form> so it can validate + read the
// preference fields. Editing the visit type or location after revealing hides availability
// again — the shown slots were for the old preferences.
function AvailabilityStep({
  program,
  booking
}: {
  program: ScreeningProgram
  booking: { isPending: boolean; isError: boolean }
}): JSX.Element {
  const { trigger, getValues, setValue } = useFormContext<PreferencesFormValues>()
  const offeringId = useWatch<PreferencesFormValues>({ name: "offeringId" })
  const region = useWatch<PreferencesFormValues>({ name: "region" })
  const stateCode = region ? String(region.value) : ""

  // The preferences the member submitted to reveal availability; null until they do.
  const [submitted, setSubmitted] = React.useState<{ offeringId: string; stateCode: string } | null>(null)

  React.useEffect(() => {
    if (submitted && (submitted.offeringId !== offeringId || submitted.stateCode !== stateCode)) {
      setSubmitted(null)
      setValue("selectedDate", null)
      setValue("selectedSlot", null)
    }
  }, [offeringId, stateCode, submitted, setValue])

  const onSeeAvailability = async () => {
    // Submitting the preferences: validate the required fields, then reveal availability.
    const valid = await trigger(["offeringId", "region"])
    if (!valid) return
    const values = getValues()
    setSubmitted({
      offeringId: values.offeringId,
      stateCode: values.region ? String(values.region.value) : ""
    })
  }

  if (!submitted) {
    return (
      <Box>
        <FormButton type="submit" color="secondary" onClick={onSeeAvailability}>
          See availability
        </FormButton>
      </Box>
    )
  }

  return (
    <AppointmentAvailability
      program={program}
      offeringId={submitted.offeringId}
      stateCode={submitted.stateCode}
      booking={booking}
    />
  )
}
